<script lang="ts" setup>
  import { useLocale } from '@tundr/i18n';
  import { disabledClass } from '@tundr/theme';
  import { computed } from 'vue';
  import Checkbox from '../../Checkbox/Checkbox.vue';
  import {
    checkableOptionCheckboxClass,
    checkableOptionClass,
    checkableOptionDisabledMarkerClass,
    checkableOptionLabelClass,
  } from './CheckableSelect.css';
  import type {
    CheckableSelectItem,
    CheckableSelectProps,
  } from './CheckableSelect.types';
  import Select from '../Select/Select.vue';

  const { t } = useLocale('ui-kit');

  const props = withDefaults(defineProps<CheckableSelectProps>(), {
    searchable: true,
    showLabels: false,
    showNoOptions: true,
    resettable: false,
    showNoResults: true,
    allowEmpty: false,
    taggable: false,
    disabled: false,
    meta: undefined,
    internalSearch: true,
    valueKey: 'value',
    labelKey: 'label',
  });

  const emit = defineEmits<{
    blur: [ev?: Event, shouldValidate?: boolean];
    addTag: [value: string];
    search: [value: CheckableSelectItem[]];
    reset: [];
    'update:modelValue': [value: CheckableSelectItem[]];
    select: [value: CheckableSelectItem[]];
  }>();

  // Configure props for underlying Select component
  const selectProps = computed(() => ({
    ...props,
    multiple: true,
    closeOnSelect: false,
    clearOnSelect: false,
    trackBy: props.valueKey,
    optionLabel: props.labelKey,
    // Custom option class function to add disabled class to multiselect option wrapper
    optionClass: (option: CheckableSelectItem) =>
      option.disabled ? 'option-disabled' : '',
  }));

  const handleChange = (value: unknown) => {
    emit('update:modelValue', value as CheckableSelectItem[]);
  };

  const handleBlur = (ev?: Event, shouldValidate?: boolean) => {
    emit('blur', ev, shouldValidate);
  };

  const handleTag = (value: string) => {
    emit('addTag', value);
  };

  const handleSearch = (value: unknown) => {
    emit('search', value as CheckableSelectItem[]);
  };

  const handleSelect = (value: unknown) => {
    emit('select', value as CheckableSelectItem[]);
  };

  const handleReset = () => {
    emit('reset');
  };

  const isOptionSelected = (option: CheckableSelectItem): boolean => {
    if (!props.modelValue || !Array.isArray(props.modelValue)) {
      return false;
    }

    const valueKey = props.valueKey || 'value';
    const optionValue = option[valueKey];

    return props.modelValue.some(
      (selected) => selected[valueKey] === optionValue,
    );
  };

  const getOptionLabel = (option: CheckableSelectItem): string => {
    const labelKey = props.labelKey || 'label';
    return String(option[labelKey]);
  };

  const getOptionValue = (option: CheckableSelectItem): string | number => {
    const valueKey = props.valueKey || 'value';
    return option[valueKey] as string | number;
  };

  const handleOptionClick = (option: CheckableSelectItem) => {
    if (props.disabled || option.disabled) return;

    const isSelected = isOptionSelected(option);
    let newValue: CheckableSelectItem[];
    const currentValue = props.modelValue || [];

    if (isSelected) {
      const valueKey = props.valueKey || 'value';
      newValue = currentValue.filter(
        (selected) => selected[valueKey] !== option[valueKey],
      );
    } else {
      newValue = [...currentValue, option];
    }

    emit('update:modelValue', newValue);
    emit('select', newValue);
  };

  defineOptions({
    inheritAttrs: false,
  });
</script>

<template>
  <!-- @ts-ignore - meta prop type variance is acceptable -->
  <Select
    v-bind="selectProps"
    class="checkable-select"
    @update:model-value="handleChange"
    @blur="handleBlur"
    @add-tag="handleTag"
    @search="handleSearch"
    @select="handleSelect"
    @reset="handleReset"
  >
    <template v-for="(_, slot) in $slots" v-slot:[slot]="scope" :key="slot">
      <slot
        v-if="slot !== 'option' && slot !== 'selection'"
        :name="slot"
        v-bind="scope"
      />
    </template>

    <template #selection="{ values, isOpen }">
      <slot
        v-if="$slots.selection"
        name="selection"
        v-bind="{ values, isOpen }"
      />
      <template v-else>
        <span class="multiselect__single" v-if="values.length" v-show="!isOpen">
          {{ t('checkable_select.optionsSelected', { count: values.length }) }}
        </span>
      </template>
    </template>

    <template #option="{ option }">
      <!-- biome-ignore lint/a11y/useKeyWithClickEvents: vue-multiselect handles arrow keys and Enter for option selection -->
      <li
        :class="[checkableOptionClass, { [disabledClass]: option.disabled }]"
        @click.stop="handleOptionClick(option)"
        :id="`listbox-${getOptionValue(option)}`"
      >
        <!-- Marker element for CSS :has() selector to disable parent option -->
        <div
          v-if="option.disabled"
          :class="[
            checkableOptionDisabledMarkerClass,
            { [disabledClass]: option.disabled },
          ]"
        ></div>

        <div :class="checkableOptionCheckboxClass">
          <Checkbox
            :name="`checkable-select-${getOptionValue(option)}`"
            :model-value="isOptionSelected(option)"
            :disabled="disabled || option.disabled"
          />
        </div>
        <span :class="checkableOptionLabelClass">
          {{ getOptionLabel(option) }}
        </span>
      </li>
    </template>
  </Select>
</template>
